import React, { useEffect } from "react";
import Header from "../../components/ui/Header";
// import ServiceHero from "./components/ServiceHero";
import ServiceCard from "./components/ServiceCard";
import ProcessSection from "./components/ProcessSection";
import PackageComparison from "./components/PackageComparison";
import TestimonialsSection from "./components/TestimonialsSection";
import ConsultationCTA from "./components/ConsultationCTA";
import Icon from "../../components/AppIcon";

const ServicesArchitectureHub = () => {
  useEffect(() => {
    document.title =
      "Services - Skycrest Studios | Professional Video Production";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      category: "events",
      title: "Event & Wedding Videography",
      description:
        "Capture life's most precious moments with cinematic elegance and emotional storytelling that preserves memories for generations.",
      icon: "Heart",
      features: [
        "Romantic cinematic storytelling",
        "Multi-camera ceremony coverage",
        "Professional audio recording",
        "Same-day preview highlights",
        "Drone cinematography included",
      ],
      sampleImage:
        "https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop",
      sampleTitle: "Sarah & Michael's Garden Wedding",
      startingPrice: "$2,500",
      typicalDuration: "6-12 hours",
    },
    {
      category: "realestate",
      title: "Real Estate Visual Marketing",
      description:
        "Showcase properties with architectural precision and sophisticated visual appeal that drives faster sales and higher prices.",
      icon: "Building",
      features: [
        "4K cinematic property tours",
        "Aerial drone photography",
        "Professional staging shots",
        "Quick 48-hour delivery",
        "Multiple format optimization",
      ],
      sampleImage:
        "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=600&h=400&fit=crop",
      sampleTitle: "Luxury Penthouse Showcase",
      startingPrice: "$800",
      typicalDuration: "2-4 hours",
    },
    {
      category: "marketing",
      title: "Brand & Marketing Content",
      description:
        "Create bold, contemporary content that cuts through the noise and drives measurable engagement with your target audience.",
      icon: "Megaphone",
      features: [
        "Multi-platform campaign creation",
        "Advanced motion graphics",
        "Professional talent casting",
        "A/B testing variations",
        "Performance analytics setup",
      ],
      sampleImage:
        "https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop",
      sampleTitle: "TechFlow Brand Campaign",
      startingPrice: "$3,500",
      typicalDuration: "1-2 weeks",
    },
    {
      category: "design",
      title: "Design Fiction & Conceptual Work",
      description:
        "Visualize tomorrow's possibilities with experimental techniques and futuristic narratives that bring complex ideas to life.",
      icon: "Zap",
      features: [
        "3D visualization & CGI",
        "Interactive element design",
        "VR/AR compatibility",
        "Prototype visualization",
        "Exhibition-ready formats",
      ],
      sampleImage:
        "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop",
      sampleTitle: "Future Labs Innovation Showcase",
      startingPrice: "$5,000",
      typicalDuration: "2-8 weeks",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      {/* <ServiceHero /> */}
      {/* Services Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="Grid3x3" size={16} />
              <span>Our Specialized Services</span>
            </div>

            <h2 className="text-fluid-4xl font-display font-bold text-primary mb-6">
              Four Distinct Approaches,
              <span className="text-accent"> One Creative Excellence</span>
            </h2>

            <p className="text-fluid-lg text-muted-foreground leading-relaxed">
              Each service category receives our full creative attention with
              specialized approaches tailored to your industry's unique needs
              and audience expectations.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
            {services?.map((service, index) => (
              <ServiceCard key={index} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>
      {/* Process Section */}
      <ProcessSection />
      {/* Package Comparison */}
      <PackageComparison />
      {/* Testimonials */}
      <TestimonialsSection />
      {/* Cross-Selling Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent-foreground px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="Link" size={16} />
              <span>Comprehensive Solutions</span>
            </div>

            <h2 className="text-fluid-4xl font-display font-bold text-primary mb-6">
              Services That Work
              <span className="text-accent"> Better Together</span>
            </h2>

            <p className="text-fluid-lg text-muted-foreground mb-12 leading-relaxed">
              Many of our clients discover that combining services creates more
              powerful, cohesive brand storytelling across all touchpoints.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-card border border-border rounded-2xl p-8 text-left">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="Building" size={24} className="text-slate-600" />
                  <Icon
                    name="Plus"
                    size={16}
                    className="text-muted-foreground"
                  />
                  <Icon
                    name="Megaphone"
                    size={24}
                    className="text-orange-600"
                  />
                </div>
                <h3 className="text-xl font-bold text-primary mb-3 font-display">
                  Real Estate + Marketing
                </h3>
                <p className="text-muted-foreground mb-4">
                  Property showcases enhanced with agent branding and marketing
                  content for comprehensive real estate campaigns.
                </p>
                <div className="text-sm text-accent font-medium">
                  Save 25% when combined
                </div>
              </div>

              <div className="bg-card border border-border rounded-2xl p-8 text-left">
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="Heart" size={24} className="text-rose-600" />
                  <Icon
                    name="Plus"
                    size={16}
                    className="text-muted-foreground"
                  />
                  <Icon
                    name="Megaphone"
                    size={24}
                    className="text-orange-600"
                  />
                </div>
                <h3 className="text-xl font-bold text-primary mb-3 font-display">
                  Events + Marketing
                </h3>
                <p className="text-muted-foreground mb-4">
                  Wedding videography paired with venue marketing content and
                  vendor promotional materials.
                </p>
                <div className="text-sm text-accent font-medium">
                  Save 20% when combined
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Consultation CTA */}
      <ConsultationCTA />
      {/* Footer */}
      <footer className="bg-primary text-primary-foreground py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="relative">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    className="text-primary-foreground"
                    fill="currentColor"
                  >
                    <path
                      d="M20 2L35 12v16L20 38L5 28V12L20 2z"
                      opacity="0.1"
                    />
                    <path
                      d="M20 6L31 14v12L20 34L9 26V14L20 6z"
                      opacity="0.3"
                    />
                    <path d="M20 10L27 16v8L20 30L13 24v-8L20 10z" />
                    <circle cx="20" cy="20" r="3" fill="var(--color-accent)" />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-bold font-display">
                    Skycrest
                  </span>
                  <span className="text-xs text-primary-foreground/70 font-medium tracking-wider">
                    STUDIOS
                  </span>
                </div>
              </div>
              <p className="text-primary-foreground/80 mb-6 max-w-md">
                Transforming visions into cinematic reality across events, real
                estate, marketing, and design fiction.
              </p>
              <div className="flex items-center space-x-4">
                {["Instagram", "Youtube", "Linkedin", "Twitter"]?.map(
                  (social) => (
                    <button
                      key={social}
                      className="w-10 h-10 bg-primary-foreground/10 hover:bg-primary-foreground/20 rounded-full flex items-center justify-center transition-colors duration-200"
                    >
                      <Icon name={social} size={18} />
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <div className="space-y-2">
                {[
                  "Event Videography",
                  "Real Estate Marketing",
                  "Brand Content",
                  "Design Fiction",
                ]?.map((service) => (
                  <button
                    key={service}
                    className="block text-primary-foreground/80 hover:text-primary-foreground transition-colors duration-200 text-left"
                  >
                    {service}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Icon name="Phone" size={16} />
                  <span className="text-primary-foreground/80">
                    (508) 980-5187
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Mail" size={16} />
                  <span className="text-primary-foreground/80">
                    hello@skycrest.com
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="MapPin" size={16} />
                  <span className="text-primary-foreground/80">
                    Rockport, MA 01966
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="text-primary-foreground/60 text-sm">
              © {new Date()?.getFullYear()} Skycrest Studios. All rights
              reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              {["Privacy Policy", "Terms of Service", "Cookie Policy"]?.map(
                (link) => (
                  <button
                    key={link}
                    className="text-primary-foreground/60 hover:text-primary-foreground text-sm transition-colors duration-200"
                  >
                    {link}
                  </button>
                )
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesArchitectureHub;
